import { useState, useEffect, useCallback, useRef } from "react";
import {
  Flame, Search, Plus, Trash2, X, CheckCircle, AlertCircle,
  ChevronLeft, ChevronRight, Settings, Apple, Calculator, Info, Pencil, Bookmark, BookmarkCheck
} from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from "recharts";
import VoedingIntakeModal from "./VoedingIntakeModal";

const API = process.env.REACT_APP_BACKEND_URL;

const MACRO_CONFIG = [
  { key: "calorieen",    label: "Calorieën", unit: "kcal", color: "#C9A84C" },
  { key: "eiwitten",     label: "Eiwitten",  unit: "g",    color: "#60a5fa" },
  { key: "koolhydraten", label: "Koolhyd.",  unit: "g",    color: "#34d399" },
  { key: "vetten",       label: "Vetten",    unit: "g",    color: "#f97316" },
];

function MacroBar({ label, huidig, doel, color, unit }) {
  const pct = doel > 0 ? Math.min((huidig / doel) * 100, 100) : 0;
  const over = doel > 0 && huidig > doel;
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-zinc-400 text-xs font-semibold">{label}</span>
        <span className="text-xs font-bold tabular-nums" style={{ color }}>
          {Math.round(huidig)}<span className="text-zinc-600 font-normal">/{Math.round(doel)} {unit}</span>
        </span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: over ? "#ef4444" : color }}
        />
      </div>
    </div>
  );
}

// Simpele herkenning of een product waarschijnlijk een vloeistof is
function isWaarschijnlijkDrank(naam = "") {
  return /melk|sap|juice|drank|water|cola|fanta|limonade|koffie|thee|smoothie|yoghurt|yogurt|kwark|drink|shake|karnemelk|chocolademelk/.test(naam.toLowerCase());
}

function VoedselZoekenModal({ token, onVoeg, onClose }) {
  const [tab, setTab] = useState("zoeken");
  // ── Zoeken ───────────────────────────────────────────────────────────────
  const [query, setQuery] = useState("");
  const [resultaten, setResultaten] = useState([]);
  const [zoeken, setZoeken] = useState(false);
  const [laadtMeer, setLaadtMeer] = useState(false);
  const [heeftMeer, setHeeftMeer] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [totaalResultaten, setTotaalResultaten] = useState(0);
  const [ahOnly, setAhOnly] = useState(false);
  const [geselecteerd, setGeselecteerd] = useState(null);
  const [gram, setGram] = useState(100);
  const [eenheid, setEenheid] = useState("g");
  // ── Handmatig ────────────────────────────────────────────────────────────
  const [handmatig, setHandmatig] = useState({ naam: "", calorieen: 0, eiwitten: 0, koolhydraten: 0, vetten: 0 });
  const [handmatigEenheid, setHandmatigEenheid] = useState("g");
  const [handmatigHoeveelheid, setHandmatigHoeveelheid] = useState(100);
  const [bewaarAlsFavoriet, setBewaarAlsFavoriet] = useState(false);
  const [opslaan, setOpslaan] = useState(false);
  // ── Opgeslagen maaltijden ────────────────────────────────────────────────
  const [eigenMaaltijden, setEigenMaaltijden] = useState([]);
  const [ladenEigen, setLadenEigen] = useState(false);
  const [verwijderendId, setVerwijderendId] = useState(null);

  const searchRef = useRef(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  // Auto-eenheid op basis van productnaam
  useEffect(() => {
    if (geselecteerd) {
      setEenheid(isWaarschijnlijkDrank(geselecteerd.naam) ? "ml" : "g");
      setGram(100);
    }
  }, [geselecteerd]);

  // Laad eigen maaltijden als tab geopend
  useEffect(() => {
    if (tab === "opgeslagen") laadEigenMaaltijden();
  }, [tab]); // eslint-disable-line

  const laadEigenMaaltijden = async () => {
    setLadenEigen(true);
    try {
      const r = await fetch(`${API}/api/nutrition/eigen-maaltijden`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setEigenMaaltijden(d.maaltijden || []);
      }
    } catch (e) { /* network error */ }
    setLadenEigen(false);
  };

  const verwijderEigenMaaltijd = async (id) => {
    setVerwijderendId(id);
    try {
      await fetch(`${API}/api/nutrition/eigen-maaltijden/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setEigenMaaltijden(prev => prev.filter(m => m.id !== id));
    } catch (e) { /* error */ }
    setVerwijderendId(null);
  };

  const zoekVoedsel = async (q, pg = 1, toevoegen = false, ahMode = ahOnly) => {
    if (q.trim().length < 2) { setResultaten([]); setHeeftMeer(false); setTotaalResultaten(0); return; }
    if (pg === 1) setZoeken(true); else setLaadtMeer(true);
    try {
      const url = `${API}/api/nutrition/voedsel/zoeken?q=${encodeURIComponent(q)}&pagina=${pg}${ahMode ? "&ah_only=true" : ""}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      const nieuweResultaten = d.producten || [];
      setResultaten(prev => toevoegen ? [...prev, ...nieuweResultaten] : nieuweResultaten);
      setHeeftMeer(d.meer || false);
      setTotaalResultaten(d.totaal || 0);
      setPagina(pg);
    } catch (e) { /* network error */ }
    setZoeken(false);
    setLaadtMeer(false);
  };

  useEffect(() => {
    const t = setTimeout(() => zoekVoedsel(query, 1, false, ahOnly), 500);
    return () => clearTimeout(t);
  }, [query, ahOnly]); // eslint-disable-line

  const bereken = (product, g, een = "g") => ({
    naam: product.naam,
    hoeveelheid: Number(g),
    eenheid: een,
    calorieen: Math.round(product.calorieen_per_100g * g / 100 * 10) / 10,
    eiwitten: Math.round(product.eiwitten_per_100g * g / 100 * 10) / 10,
    koolhydraten: Math.round(product.koolhydraten_per_100g * g / 100 * 10) / 10,
    vetten: Math.round(product.vetten_per_100g * g / 100 * 10) / 10,
    bron: "openfoodfacts",
  });

  const handleHandmatigToevoegen = async () => {
    const maaltijdData = {
      ...handmatig,
      hoeveelheid: handmatigHoeveelheid,
      eenheid: handmatigEenheid,
      bron: "handmatig",
    };
    if (bewaarAlsFavoriet) {
      setOpslaan(true);
      try {
        await fetch(`${API}/api/nutrition/eigen-maaltijden`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            naam: handmatig.naam,
            calorieen: handmatig.calorieen,
            eiwitten: handmatig.eiwitten,
            koolhydraten: handmatig.koolhydraten,
            vetten: handmatig.vetten,
            eenheid: handmatigEenheid,
            hoeveelheid: handmatigHoeveelheid,
          }),
        });
      } catch (e) { /* silently fail */ }
      setOpslaan(false);
    }
    onVoeg(maaltijdData);
    onClose();
  };

  const inputCls = "bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-base focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600";

  const TABS = [
    { key: "zoeken",    label: "Zoeken" },
    { key: "handmatig", label: "Handmatig" },
    { key: "opgeslagen", label: "Opgeslagen" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-md max-h-[88vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/8 shrink-0">
          <h3 className="text-white font-bold">Maaltijd toevoegen</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-3 border-b border-white/5 shrink-0">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${tab === t.key ? "text-black" : "text-zinc-400"}`}
              style={tab === t.key ? { background: "#C9A84C" } : { background: "#1a1a1a" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Zoeken tab ─────────────────────────────────────────────────── */}
        {tab === "zoeken" && (
          <>
            <div className="p-3 border-b border-white/5 shrink-0 space-y-2">
              <div className="relative">
                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={ahOnly ? "Zoek in Albert Heijn assortiment..." : "Zoek product... (bijv. kip, rijst, ei)"}
                  data-testid="voedsel-zoek-input"
                  className={`${inputCls} w-full pl-9`}
                />
              </div>
              {/* AH toggle */}
              <button
                onClick={() => { setAhOnly(v => !v); setResultaten([]); }}
                data-testid="ah-only-toggle"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                  ahOnly
                    ? "bg-[#00A0E2]/15 border-[#00A0E2]/40 text-[#00A0E2]"
                    : "bg-white/5 border-white/10 text-zinc-500 hover:text-zinc-300"
                }`}>
                <span className="font-bold text-[10px] tracking-wide">AH</span>
                {ahOnly ? "Albert Heijn actief" : "Zoek in Albert Heijn"}
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-3">
              {zoeken ? (
                <p className="text-zinc-600 text-sm text-center py-6">Zoeken...</p>
              ) : resultaten.length === 0 && query.length >= 2 ? (
                <p className="text-zinc-600 text-sm text-center py-6">Geen resultaten voor &ldquo;{query}&rdquo;</p>
              ) : geselecteerd ? (
                <div className="space-y-4">
                  <div className="bg-[#1a1a1a] rounded-xl p-3">
                    <p className="text-white font-semibold text-sm mb-1">{geselecteerd.naam}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-zinc-500">
                      {MACRO_CONFIG.map(m => (
                        <span key={m.key}>
                          {m.label}: <span className="font-bold" style={{ color: m.color }}>
                            {Math.round((geselecteerd[`${m.key}_per_100g`] || 0) * gram / 100 * 10) / 10} {m.unit}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* Eenheid toggle */}
                  <div>
                    <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">
                      Eenheid
                    </label>
                    <div className="flex gap-2 mb-3">
                      {["g", "ml"].map(e => (
                        <button
                          key={e}
                          onClick={() => setEenheid(e)}
                          data-testid={`eenheid-toggle-${e}`}
                          className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${
                            eenheid === e
                              ? "text-black border-transparent"
                              : "bg-[#1a1a1a] border-white/10 text-zinc-400 hover:text-white"
                          }`}
                          style={eenheid === e ? { background: "#C9A84C" } : {}}>
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">
                      Hoeveelheid ({eenheid})
                    </label>
                    <input
                      type="number"
                      value={gram}
                      onChange={e => setGram(Number(e.target.value))}
                      min={1}
                      data-testid="gram-input"
                      className={`${inputCls} w-full`}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setGeselecteerd(null)}
                      className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold">
                      Terug
                    </button>
                    <button
                      onClick={() => { onVoeg(bereken(geselecteerd, gram, eenheid)); onClose(); }}
                      data-testid="voeg-maaltijd-toe-btn"
                      className="flex-1 py-2.5 rounded-xl text-black text-sm font-bold"
                      style={{ background: "#C9A84C" }}>
                      Toevoegen
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {resultaten.length > 0 && (
                    <p className="text-zinc-600 text-[11px] px-1 mb-2">
                      {resultaten.length} producten{ahOnly ? " van Albert Heijn" : ""}{totaalResultaten > 25 ? ` (van ±${totaalResultaten})` : ""}
                    </p>
                  )}
                  {resultaten.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => !p.voeding_onbekend && setGeselecteerd(p)}
                      data-testid={`voedsel-resultaat-${i}`}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border bg-[#1a1a1a] text-left transition-all group ${
                        p.voeding_onbekend
                          ? "border-white/5 opacity-60 cursor-not-allowed"
                          : "border-white/5 hover:border-[#C9A84C]/30 cursor-pointer"
                      }`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate group-hover:text-[#C9A84C] transition-colors">{p.naam}</p>
                        {p.merk && <p className="text-zinc-600 text-xs truncate">{p.merk}</p>}
                        {p.voeding_onbekend ? (
                          <p className="text-zinc-700 text-[10px] mt-0.5">Voedingswaarden niet beschikbaar in database</p>
                        ) : (
                          <div className="flex gap-3 mt-1">
                            <span className="text-zinc-500 text-[10px]">E: <span className="text-blue-400 font-semibold">{p.eiwitten_per_100g}g</span></span>
                            <span className="text-zinc-500 text-[10px]">K: <span className="text-emerald-400 font-semibold">{p.koolhydraten_per_100g}g</span></span>
                            <span className="text-zinc-500 text-[10px]">V: <span className="text-orange-400 font-semibold">{p.vetten_per_100g}g</span></span>
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        {p.voeding_onbekend ? (
                          <p className="text-zinc-700 text-xs">?</p>
                        ) : (
                          <>
                            <p className="text-[#C9A84C] text-sm font-bold">{p.calorieen_per_100g}</p>
                            <p className="text-zinc-600 text-[10px]">kcal/100g</p>
                          </>
                        )}
                      </div>
                    </button>
                  ))}
                  {heeftMeer && (
                    <button
                      onClick={() => zoekVoedsel(query, pagina + 1, true)}
                      disabled={laadtMeer}
                      data-testid="meer-laden-btn"
                      className="w-full py-3 rounded-xl border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 text-sm font-semibold transition-all disabled:opacity-50">
                      {laadtMeer ? "Laden..." : "Meer resultaten laden"}
                    </button>
                  )}
                  {resultaten.length === 0 && query.length === 0 && (
                    <div className="text-center py-8">
                      <Apple size={28} className="text-zinc-700 mx-auto mb-2" />
                      <p className="text-zinc-500 text-sm">Typ om te zoeken</p>
                      <p className="text-zinc-700 text-xs mt-1">bijv. kip, rijst, banaan, melk</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Handmatig tab ────────────────────────────────────────────────── */}
        {tab === "handmatig" && (
          <div className="p-5 space-y-4 overflow-y-auto flex-1">
            <div>
              <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">Naam *</label>
              <input
                value={handmatig.naam}
                onChange={e => setHandmatig(h => ({ ...h, naam: e.target.value }))}
                placeholder="bv. Zelfgemaakte smoothie"
                className={`${inputCls} w-full`}
              />
            </div>

            {/* Eenheid + hoeveelheid */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">Eenheid</label>
                <div className="flex gap-2">
                  {["g", "ml"].map(e => (
                    <button
                      key={e}
                      onClick={() => setHandmatigEenheid(e)}
                      data-testid={`handmatig-eenheid-${e}`}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all border ${
                        handmatigEenheid === e
                          ? "text-black border-transparent"
                          : "bg-[#1a1a1a] border-white/10 text-zinc-400 hover:text-white"
                      }`}
                      style={handmatigEenheid === e ? { background: "#C9A84C" } : {}}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">
                  Hoeveelheid ({handmatigEenheid})
                </label>
                <input
                  type="number"
                  value={handmatigHoeveelheid}
                  onChange={e => setHandmatigHoeveelheid(Number(e.target.value))}
                  min={1}
                  data-testid="handmatig-hoeveelheid-input"
                  className={`${inputCls} w-full`}
                />
              </div>
            </div>

            {/* Macros grid */}
            <div className="grid grid-cols-2 gap-3">
              {MACRO_CONFIG.map(m => (
                <div key={m.key}>
                  <label className="text-xs font-semibold mb-1.5 block" style={{ color: m.color }}>
                    {m.label} ({m.unit})
                  </label>
                  <input
                    type="number"
                    value={handmatig[m.key]}
                    onChange={e => setHandmatig(h => ({ ...h, [m.key]: Number(e.target.value) }))}
                    min={0}
                    step={0.1}
                    className={`${inputCls} w-full`}
                  />
                </div>
              ))}
            </div>

            {/* Bewaar als favoriet */}
            <button
              onClick={() => setBewaarAlsFavoriet(v => !v)}
              data-testid="bewaar-als-favoriet-toggle"
              className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                bewaarAlsFavoriet
                  ? "bg-[#C9A84C]/10 border-[#C9A84C]/30 text-[#C9A84C]"
                  : "bg-[#1a1a1a] border-white/8 text-zinc-400 hover:text-white"
              }`}>
              {bewaarAlsFavoriet
                ? <BookmarkCheck size={15} />
                : <Bookmark size={15} />
              }
              <span className="text-sm font-semibold">
                {bewaarAlsFavoriet ? "Wordt opgeslagen als favoriet" : "Bewaar als favoriet voor hergebruik"}
              </span>
            </button>

            <button
              disabled={!handmatig.naam.trim() || opslaan}
              onClick={handleHandmatigToevoegen}
              data-testid="voeg-handmatig-toe-btn"
              className="w-full py-3 rounded-xl text-black font-bold text-sm disabled:opacity-40"
              style={{ background: "#C9A84C" }}>
              {opslaan ? "Opslaan..." : "Toevoegen"}
            </button>
          </div>
        )}

        {/* ── Opgeslagen maaltijden tab ─────────────────────────────────────── */}
        {tab === "opgeslagen" && (
          <div className="overflow-y-auto flex-1 p-4 space-y-2">
            {ladenEigen ? (
              <p className="text-zinc-600 text-sm text-center py-8">Laden...</p>
            ) : eigenMaaltijden.length === 0 ? (
              <div className="text-center py-10">
                <Bookmark size={28} className="text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">Nog geen opgeslagen maaltijden</p>
                <p className="text-zinc-700 text-xs mt-1">
                  Voeg een handmatige maaltijd toe en vink &ldquo;Bewaar als favoriet&rdquo; aan
                </p>
              </div>
            ) : (
              <>
                <p className="text-zinc-600 text-[11px] px-1 mb-2">{eigenMaaltijden.length} opgeslagen maaltijden</p>
                {eigenMaaltijden.map(m => (
                  <div key={m.id}
                    data-testid={`eigen-maaltijd-${m.id}`}
                    className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-white/5 rounded-xl group">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{m.naam}</p>
                      <p className="text-zinc-600 text-xs mt-0.5">
                        {m.hoeveelheid}{m.eenheid} ·{" "}
                        <span className="text-[#C9A84C]">{Math.round(m.calorieen)} kcal</span>
                        {" · "}{Math.round(m.eiwitten)}g E · {Math.round(m.koolhydraten)}g K · {Math.round(m.vetten)}g V
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => { onVoeg({ ...m, bron: "opgeslagen" }); onClose(); }}
                        data-testid={`gebruik-eigen-maaltijd-${m.id}`}
                        className="px-3 py-1.5 rounded-lg text-black text-xs font-bold transition-opacity hover:opacity-90"
                        style={{ background: "#C9A84C" }}>
                        Voeg toe
                      </button>
                      <button
                        onClick={() => verwijderEigenMaaltijd(m.id)}
                        disabled={verwijderendId === m.id}
                        data-testid={`verwijder-eigen-maaltijd-${m.id}`}
                        className="p-1.5 text-zinc-600 hover:text-red-400 transition-colors disabled:opacity-40">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DoelenModal({ doelen, token, datum, onClose, onSave }) {
  const [form, setForm] = useState({ ...doelen });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: Number(v) }));
  const inputCls = "bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-base focus:outline-none focus:border-[#C9A84C]/50 w-full";

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/nutrition/doelen`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error("Opslaan mislukt");
      onSave(form);
    } catch (e) { /* save error */ }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <h3 className="text-white font-bold">Macro doelen instellen</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {MACRO_CONFIG.map(m => (
            <div key={m.key}>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: m.color }}>
                {m.label} ({m.unit})
              </label>
              <input type="number" value={form[m.key]} onChange={e => set(m.key, e.target.value)}
                min={0} className={inputCls} data-testid={`doel-${m.key}-input`} />
            </div>
          ))}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold">Annuleren</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-doelen-btn"
              className="flex-1 py-2.5 rounded-xl text-black text-sm font-bold disabled:opacity-40"
              style={{ background: "#C9A84C" }}>
              {saving ? "..." : "Opslaan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VoedingSection({ user }) {
  const [datum, setDatum] = useState(new Date().toISOString().split("T")[0]);
  const [logData, setLogData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showZoeken, setShowZoeken] = useState(false);
  const [showDoelen, setShowDoelen] = useState(false);
  const [showIntake, setShowIntake] = useState(false);
  const [intake, setIntake] = useState(null);
  const [berekening, setBerekening] = useState(null);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState("vandaag");

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async (d = datum) => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/api/nutrition/log?datum=${d}`, { headers }),
        fetch(`${API}/api/nutrition/overzicht`, { headers }),
        fetch(`${API}/api/nutrition/intake`, { headers }),
      ]);
      if (r1.ok) setLogData(await r1.json());
      if (r2.ok) {
        const d2 = await r2.json();
        setChartData(d2.data || []);
      }
      if (r3.ok) {
        const id = await r3.json();
        setIntake(id.intake || null);
        setBerekening(id.berekening || null);
      }
    } catch (e) { /* load error */ }
    setLoading(false);
  }, [user?.token]); // eslint-disable-line

  useEffect(() => { load(datum); }, [datum]); // eslint-disable-line

  const navigeerDag = (delta) => {
    const d = new Date(datum);
    d.setDate(d.getDate() + delta);
    const newDatum = d.toISOString().split("T")[0];
    if (newDatum <= new Date().toISOString().split("T")[0]) {
      setDatum(newDatum);
    }
  };

  const voegMaaltijdToe = async (maaltijd) => {
    try {
      const r = await fetch(`${API}/api/nutrition/log/maaltijd?datum=${datum}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
        body: JSON.stringify(maaltijd),
      });
      if (!r.ok) throw new Error("Toevoegen mislukt");
      showToast(`${maaltijd.naam} toegevoegd`);
      await load(datum);
    } catch (e) { showToast(e.message, "error"); }
  };

  const verwijderMaaltijd = async (maaltijdId) => {
    try {
      const r = await fetch(`${API}/api/nutrition/log/maaltijd/${maaltijdId}?datum=${datum}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (!r.ok) throw new Error("Verwijderen mislukt");
      await load(datum);
    } catch (e) { showToast(e.message, "error"); }
  };

  const vandaag = new Date().toISOString().split("T")[0];
  const isVandaag = datum === vandaag;
  const datumLabel = isVandaag ? "Vandaag" : new Date(datum).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" });
  const { totalen = {}, doelen = {}, maaltijden = [] } = logData || {};

  return (
    <div className="account-card mb-6" data-testid="voeding-section">
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Gezondheid</p>
          <h2 className="text-white font-bold text-xl">Voeding</h2>
          <p className="text-zinc-500 text-sm mt-0.5">Macros bijhouden</p>
        </div>
        <button
          onClick={() => setShowDoelen(true)}
          data-testid="open-doelen-btn"
          className="p-2.5 rounded-xl border border-white/10 text-zinc-500 hover:text-white hover:border-white/20 transition-all">
          <Settings size={15} />
        </button>
      </div>

      {/* ── Voedingsprofiel / Intake kaart ────────────────────────────── */}
      {!loading && (
        <div className={`rounded-2xl border p-4 mb-5 ${intake ? "bg-[#C9A84C]/6 border-[#C9A84C]/20" : "bg-[#1a1a1a] border-white/6 border-dashed"}`}>
          {intake && berekening ? (
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-2">
                  <Calculator size={12} className="text-[#C9A84C]" />
                  <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Voedingsprofiel</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-zinc-500 text-[10px]">Calorieën</p>
                    <p className="text-white font-black text-lg tabular-nums">{berekening.calorie_doel}</p>
                    <p className="text-zinc-600 text-[10px]">kcal/dag</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-[10px]">Eiwitten</p>
                    <p className="text-[#60a5fa] font-black text-lg tabular-nums">{berekening.eiwitten_doel}g</p>
                    <p className="text-zinc-600 text-[10px]">per dag</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-[10px]">Doel</p>
                    <p className="text-white font-bold text-sm capitalize">{intake.doel?.replace('_',' ')}</p>
                    <p className="text-zinc-600 text-[10px]">{intake.gewicht} kg</p>
                  </div>
                </div>
              </div>
              <button onClick={() => setShowIntake(true)} data-testid="bewerk-intake-btn"
                className="p-2 rounded-xl bg-[#C9A84C]/10 border border-[#C9A84C]/20 text-[#C9A84C] hover:bg-[#C9A84C]/20 transition-all shrink-0">
                <Pencil size={12} />
              </button>
            </div>
          ) : (
            <div className="text-center py-2">
              <Info size={18} className="text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm font-semibold mb-0.5">Voedingsprofiel invullen</p>
              <p className="text-zinc-600 text-xs mb-3 leading-snug">
                Vul je gegevens in voor een persoonlijk calorieën- en macro-advies
              </p>
              <button onClick={() => setShowIntake(true)} data-testid="start-intake-btn"
                className="px-4 py-2 rounded-xl text-black text-xs font-bold"
                style={{ background: "#C9A84C" }}>
                <Calculator size={11} className="inline mr-1.5" />Profiel invullen
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#1a1a1a] border border-white/5 rounded-xl p-1 mb-5">
        {[{ key: "vandaag", label: "Daglog" }, { key: "week", label: "7 Dagen" }].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === t.key ? "text-black" : "text-zinc-400 hover:text-white"}`}
            style={activeTab === t.key ? { background: "#C9A84C" } : {}}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "vandaag" ? (
        <>
          {/* Dag navigator */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => navigeerDag(-1)}
              className="p-2 rounded-xl border border-white/8 text-zinc-500 hover:text-white transition-all">
              <ChevronLeft size={14} />
            </button>
            <span className="text-white font-bold text-sm">{datumLabel}</span>
            <button onClick={() => navigeerDag(1)} disabled={isVandaag}
              className="p-2 rounded-xl border border-white/8 text-zinc-500 hover:text-white disabled:opacity-30 transition-all">
              <ChevronRight size={14} />
            </button>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="h-8 bg-[#1a1a1a] rounded-xl animate-pulse" />)}
            </div>
          ) : (
            <>
              {/* Calorieën ring summary */}
              <div className="bg-[#1a1a1a] rounded-2xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Flame size={16} className="text-[#C9A84C]" />
                    <span className="text-white font-black text-2xl tabular-nums">{Math.round(totalen.calorieen || 0)}</span>
                    <span className="text-zinc-600 text-sm">/ {Math.round(doelen.calorieen || 2000)} kcal</span>
                  </div>
                  {doelen.calorieen > 0 && (
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${(totalen.calorieen || 0) > doelen.calorieen ? "bg-red-500/10 text-red-400" : "bg-[#C9A84C]/10 text-[#C9A84C]"}`}>
                      {Math.round(((totalen.calorieen || 0) / doelen.calorieen) * 100)}%
                    </span>
                  )}
                </div>
                <div className="space-y-2.5">
                  {MACRO_CONFIG.slice(1).map(m => (
                    <MacroBar key={m.key} label={m.label} huidig={totalen[m.key] || 0}
                      doel={doelen[m.key] || 0} color={m.color} unit={m.unit} />
                  ))}
                </div>
              </div>

              {/* Maaltijden lijst */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-zinc-400 text-xs font-semibold uppercase tracking-wide">
                  Maaltijden ({maaltijden.length})
                </span>
                <button
                  onClick={() => setShowZoeken(true)}
                  data-testid="voeg-maaltijd-toe-open-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-black text-xs font-bold"
                  style={{ background: "#C9A84C" }}>
                  <Plus size={12} /> Toevoegen
                </button>
              </div>

              {maaltijden.length === 0 ? (
                <div className="text-center py-6 bg-[#1a1a1a] rounded-xl">
                  <Apple size={24} className="text-zinc-700 mx-auto mb-2" />
                  <p className="text-zinc-500 text-sm">Nog niets gelogd</p>
                  <button onClick={() => setShowZoeken(true)}
                    className="text-[#C9A84C] text-xs font-semibold mt-1 hover:underline">
                    Eerste maaltijd toevoegen →
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {maaltijden.map(m => (
                    <div key={m.id} className="flex items-center gap-3 bg-[#1a1a1a] rounded-xl px-3.5 py-3"
                      data-testid={`maaltijd-item-${m.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{m.naam}</p>
                        <p className="text-zinc-600 text-xs">
                          {m.hoeveelheid}{m.eenheid} · <span className="text-[#C9A84C]">{Math.round(m.calorieen)} kcal</span>
                          {" · "}{Math.round(m.eiwitten)}g eiwit · {Math.round(m.koolhydraten)}g koolh. · {Math.round(m.vetten)}g vet
                        </p>
                      </div>
                      <button onClick={() => verwijderMaaltijd(m.id)}
                        data-testid={`verwijder-maaltijd-btn-${m.id}`}
                        className="text-zinc-700 hover:text-red-400 transition-colors p-1">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      ) : (
        /* 7-day chart */
        <div>
          <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-4">Calorieën afgelopen 7 dagen</p>
          {chartData.length === 0 ? (
            <p className="text-zinc-600 text-sm text-center py-8">Nog geen data</p>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis dataKey="dag" tick={{ fill: "#666", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 10, fontSize: 12 }}
                  formatter={(v) => [`${Math.round(v)} kcal`, "Calorieën"]}
                  labelStyle={{ color: "#C9A84C", fontWeight: "bold" }}
                />
                {logData?.doelen?.calorieen && (
                  <ReferenceLine y={logData.doelen.calorieen} stroke="#C9A84C" strokeDasharray="4 4" />
                )}
                <Bar dataKey="calorieen" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.datum === vandaag ? "#C9A84C" : "#C9A84C44"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="mt-4 grid grid-cols-2 gap-3">
            {MACRO_CONFIG.slice(1).map(m => {
              const totaal = chartData.reduce((s, d) => s + (d[m.key] || 0), 0);
              const gem = chartData.length > 0 ? totaal / chartData.length : 0;
              return (
                <div key={m.key} className="bg-[#1a1a1a] rounded-xl p-3">
                  <p className="text-xs font-semibold mb-0.5" style={{ color: m.color }}>{m.label}</p>
                  <p className="text-white font-black text-lg tabular-nums">{Math.round(gem)}<span className="text-zinc-600 text-xs ml-0.5">{m.unit}/dag</span></p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-xs z-50 flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold shadow-2xl ${toast.type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
          {toast.type === "success" ? <CheckCircle size={15} className="text-[#C9A84C]" /> : <AlertCircle size={15} className="text-red-400" />}
          {toast.msg}
        </div>
      )}

      {/* Modals */}
      {showZoeken && (
        <VoedselZoekenModal token={user.token} onVoeg={voegMaaltijdToe} onClose={() => setShowZoeken(false)} />
      )}
      {showDoelen && logData?.doelen && (
        <DoelenModal
          doelen={logData.doelen}
          token={user.token}
          datum={datum}
          onClose={() => setShowDoelen(false)}
          onSave={(nieuw) => {
            setLogData(prev => prev ? { ...prev, doelen: nieuw } : prev);
            setShowDoelen(false);
            showToast("Doelen opgeslagen!");
          }}
        />
      )}
      {showIntake && (
        <VoedingIntakeModal
          token={user.token}
          sporter={null}
          initialData={intake}
          initialBerekening={berekening}
          onClose={() => setShowIntake(false)}
          onSaved={(ber) => {
            setBerekening(ber);
            setShowIntake(false);
            showToast("Voedingsprofiel opgeslagen! Macro-doelen zijn bijgewerkt.");
            load(datum);
          }}
        />
      )}
    </div>
  );
}
